<template>
  <div class="condition-item">
    <!-- 条件字段：展示 fieldLabel（可读业务名），选中后表单只存 fieldName 供表达式求值 -->
    <el-select v-model="condition.fieldName" @change="changeForm">
      <el-option v-for="(item, idx) in flowForms" :value="item.fieldName" :label="item.formLabel" :key="idx"/>
    </el-select>

    <!-- 判断规则：按字段类型给不同的规则集 -->
    <el-select v-model="condition.judgeType" @change="changeJudgeType">
      <el-option v-for="rule in judgeRules" :key="rule.value" :value="rule.value" :label="rule.label"/>
    </el-select>

    <!-- 取值控件：按字段类型渲染 -->
    <!--
      数据字典：选项从字典接口按 field_code 指定的类型拉取。
      值存 userCode（如 TRAVEL），与业务表里存的口径一致，引擎直接做字符串相等即可。
    -->
    <el-select v-model="condition.fieldValue" v-if="condition.fieldType === FIELD_TYPE.DICTIONARY"
               @change="changeValue" placeholder="请选择" clearable filterable style="width: 100%">
      <el-option v-for="d in dictOptions" :key="d.userCode" :label="d.dictName" :value="d.userCode"/>
    </el-select>
    <div class="item" v-else-if="condition.fieldType === FIELD_TYPE.EMPLOYEE">
      <employee-select v-model="condition.fieldValue" @change="changeValue"/>
    </div>
    <div class="item" v-else-if="condition.fieldType === FIELD_TYPE.CUSTOMER">
      <customer-select v-model="condition.fieldValue" @change="changeValue" style="width: 100%"/>
    </div>
    <!-- 布尔：只能等于真/假，不需要自由输入 -->
    <el-select v-model="condition.fieldValue" v-else-if="condition.fieldType === FIELD_TYPE.BOOLEAN"
               @change="changeValue" placeholder="请选择">
      <el-option label="是" value="true"/>
      <el-option label="否" value="false"/>
    </el-select>
    <!-- 日期 -->
    <yq-date-picker v-else-if="condition.fieldType === FIELD_TYPE.DATE"
                    v-model="condition.fieldValue" @change="changeValue"/>
    <!-- 数值 -->
    <el-input-number v-else-if="condition.fieldType === FIELD_TYPE.NUMBER"
                     v-model="condition.fieldValue" :controls="false" @change="changeValue"
                     placeholder="请输入数值" style="width: 100%"/>
    <!-- 其它（字符串 / ID） -->
    <el-input v-else v-model="condition.fieldValue" @change="changeValue"/>

    <yq-icon name="element-ui_close" class="condition-item-remove" @click="remove"/>
  </div>
</template>

<script>
import EmployeeSelect from "@/components/h5/data/EmployeeSelect.vue";
import CustomerSelect from "@/components/h5/partner/CustomerSelect.vue";
import YqDatePicker from "@/components/h5/date/YqDatePicker.vue";
import YqIcon from "@/components/YqIcon.vue";
import commonApi from "@/api/commonApi";

/** 字段类型，与后端 wf_process_business_field.field_type 保持一致 */
const FIELD_TYPE = {
  NUMBER: 100,     // 数值
  STRING: 200,     // 字符串
  DATE: 300,       // 日期
  BOOLEAN: 400,    // 布尔
  ID: 500,         // ID(关联键)
  CUSTOMER: 600,   // 客户
  EMPLOYEE: 700,   // 职员
  DICTIONARY: 800, // 数据字典（值取字典的 userCode）
  BUSINESS_NODE: 900 // 业务节点（不参与条件判断）
};

/** 判断规则 */
const JUDGE = {
  EQ: 1,   // 等于
  GT: 2,   // 大于
  LT: 3,   // 小于
  LTE: 4,  // 小于或等于
  GTE: 5   // 大于或等于
};

/**
 * 各类型可用的判断规则。
 * 数值支持大小比较；日期只做先后顺序比较（没有"小于"的语义，用早于/晚于表达）；
 * 其余类型（字符串/ID/客户/职员/布尔）只判断相等。
 */
const JUDGE_RULES = {
  [FIELD_TYPE.NUMBER]: [
    {value: JUDGE.EQ, label: "等于"},
    {value: JUDGE.GT, label: "大于"},
    {value: JUDGE.LT, label: "小于"},
    {value: JUDGE.LTE, label: "小于或等于"},
    {value: JUDGE.GTE, label: "大于或等于"}
  ],
  [FIELD_TYPE.DATE]: [
    {value: JUDGE.EQ, label: "等于"},
    {value: JUDGE.GT, label: "大于"},
    {value: JUDGE.LT, label: "小于"}
  ],
  [FIELD_TYPE.BOOLEAN]: [
    {value: JUDGE.EQ, label: "等于"}
  ]
};

/** 默认规则集：只判断相等 */
const DEFAULT_RULES = [{value: JUDGE.EQ, label: "等于"}];

export default {
  emits: ["remove", "change"],
  props: {
    // 审批触发条件
    fieldCondition: {default: () => {
      return {fieldName: "", judgeType: "", fieldValue: ""};
      }
    },
    // 流程可用的字段列表（来自「业务设置」）
    flowForms: {default: () => []},
    // 序号，用于列表数据
    index: {default: () => [0]},
    // 层级
    level: {default: 0},
  },
  name: "NodeCheckCondition",
  components: {
    YqIcon,
    EmployeeSelect,
    CustomerSelect,
    YqDatePicker
  },
  watch: {
    fieldCondition: {
      handler() {
        this.condition = {...this.fieldCondition};
        this.init();
      }
    },
    flowForms: {
      handler() {
        this.condition = {...this.fieldCondition};
        this.init();
      }
    }
  },
  data() {
    return {
      FIELD_TYPE,
      condition: {
        fieldName: "",
        judgeType: "",
        fieldValue: "",
        fieldType: null
      },
      judgeRules: DEFAULT_RULES,
      // 数据字典字段的候选项，来自字典接口
      dictOptions: [],
      // 已加载的字典类型，避免同一类型重复请求
      loadedDictType: "",
    };
  },
  created() {
    this.condition = {...this.fieldCondition};
    this.init();
  },
  methods: {
    init() {
      // 在可用字段里找到当前选中的那个，拿到它的类型
      let hit = null;
      for (let i = 0; i < this.flowForms.length; i++) {
        if (this.flowForms[i].fieldName === this.condition.fieldName) {
          hit = this.flowForms[i];
          break;
        }
      }
      this.condition.fieldType = hit ? hit.formType : null;
      this.judgeRules = JUDGE_RULES[this.condition.fieldType] || DEFAULT_RULES;
      // 切换字段后，原先的判断规则可能已不适用（如从数值换成字符串），
      // 此时重置为「等于」并清空取值，避免留下无效组合
      if (!this.judgeRules.some(r => r.value === this.condition.judgeType)) {
        this.condition.judgeType = JUDGE.EQ;
      }
      // 字典字段才需要拉选项；其余类型（含切走字典的情况）清空即可
      if (this.condition.fieldType === FIELD_TYPE.DICTIONARY && hit) {
        this.loadDictOptions(hit.fieldCode);
      } else {
        this.dictOptions = [];
        this.loadedDictType = "";
      }
    },
    /**
     * 拉取字典选项。
     *
     * <p>按 fieldCode 指定的字典类型取整类，而不是按已选值去筛 ——
     * 条件配置的前提就是「值还没定」，需要把全部候选摆出来让人选。
     *
     * <p>同一类型只请求一次：一个流程里可能有多个条件引用同类字典。
     */
    loadDictOptions(dictType) {
      if (!dictType || this.loadedDictType === dictType) {
        return;
      }
      this.loadedDictType = dictType;
      commonApi.queryUniqueDictionaryList(dictType).then(list => {
        this.dictOptions = list || [];
      }).catch(() => {
        // 失败时清空并允许重试，避免卡在「已加载」状态导致永远拿不到选项
        this.dictOptions = [];
        this.loadedDictType = "";
      });
    },
    // 数据发生变更
    changeForm() {
      this.init();
      this.changeData();
    },
    changeJudgeType() {
      this.changeData();
    },
    changeValue() {
      this.changeData();
    },
    changeData() {
      this.$emit("change", this.level, this.index, this.condition);
    },
    remove() {
      this.$emit("remove", this.level, this.index);
    }
  }
};
</script>

<style lang="scss" scoped>
.condition-item{
  position: relative;
  display: flex;
  justify-content: space-between;
  margin: 20px 0;
  .item{
    width: 30%;
  }
  ::v-deep .el-select{
    width: 100%;
  }
  ::v-deep>.el-select{
    width: 30%;
  }
  ::v-deep>.el-input{
    width: 30%;
  }
  ::v-deep>.el-input-number{
    width: 30%;
  }
  .condition-item-remove{
    line-height: 30px;
    display: inline-block;
    height: 30px;
    color: var(--el-color-danger);
    font-size: 18px;
  }
}
</style>
